import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"shadow-theme-xs bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
				// Alias for backward compatibility
				primary:
					"shadow-theme-xs bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
				destructive:
					"bg-error-600 shadow-theme-xs hover:bg-error-700 disabled:bg-error-300 text-white",
				// Alias for backward compatibility
				danger:
					"bg-error-600 shadow-theme-xs hover:bg-error-700 disabled:bg-error-300 text-white",
				outline:
					"shadow-theme-xs border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/3 dark:hover:text-gray-300",
				secondary:
					"bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
				ghost:
					"hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100",
				link: "text-brand-500 dark:text-brand-400 underline-offset-4 hover:underline",
			},
			size: {
				default: "h-10 px-5 py-2.5",
				// Alias for backward compatibility
				md: "h-10 px-5 py-2.5",
				sm: "h-9 px-3 py-2",
				lg: "h-11 px-8 py-3",
				icon: "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean;
	/** @deprecated Use children with icon directly (icons have gap styling) */
	startIcon?: React.ReactNode;
	/** @deprecated Use children with icon directly (icons have gap styling) */
	endIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			isLoading = false,
			disabled,
			children,
			startIcon,
			endIcon,
			...props
		},
		ref,
	) => {
		const isDisabled = disabled || isLoading;

		// Slot requires exactly one child element, so the spinner/icon slots —
		// which render as `false` when unused — must not be handed to it.
		// `asChild` styles whatever the caller passed and nothing else.
		if (asChild) {
			return (
				<Slot
					className={cn(buttonVariants({ variant, size, className }))}
					ref={ref}
					{...props}
				>
					{children}
				</Slot>
			);
		}

		return (
			<button
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				disabled={isDisabled}
				{...props}
			>
				{isLoading && (
					<svg className="size-4 animate-spin" viewBox="0 0 24 24">
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
							fill="none"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
				)}
				{!isLoading && startIcon && (
					<span className="flex items-center">{startIcon}</span>
				)}
				{children}
				{!isLoading && endIcon && (
					<span className="flex items-center">{endIcon}</span>
				)}
			</button>
		);
	},
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
